import { useEffect, useRef, useState } from "react";
import { errorText } from "./user-error";

/** One in-flight request; inactive windows stop polling and resume on focus. */
export function useWorkspace<T>(request: () => Promise<T>, interval = 15000, enabled = true) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const previousRequest = useRef(request);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    let pending = false;
    let timer: ReturnType<typeof setTimeout>;
    if (previousRequest.current !== request) setData(undefined);
    previousRequest.current = request;
    setError("");
    const load = async () => {
      if (pending || !live) return;
      clearTimeout(timer);
      if (document.hidden) return;
      pending = true;
      try {
        const result = await request();
        if (live) {
          setData(result);
          setError("");
        }
      } catch (e) {
        if (live) setError(errorText(e));
      } finally {
        pending = false;
        if (live) timer = setTimeout(load, interval);
      }
    };
    void load();
    document.addEventListener("visibilitychange", load);
    return () => {
      live = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", load);
    };
  }, [request, interval, refresh, enabled]);
  return { data, error, setData, refresh: () => setRefresh((value) => value + 1) };
}
